/**
 * Eksport list do CSV / Excel / PDF (konwencja BillVis: na KAŻDEJ liście).
 *
 * Biblioteki są ładowane **dynamicznie** — trafiają do osobnych chunków i nie obciążają
 * wejścia do aplikacji. CSV nie wymaga żadnej zależności.
 *
 * PDF robimy **pdfmake**, nie jsPDF: jsPDF ma tylko czcionki Base-14 kodowane cp1252
 * (sprawdzone: brak `ToUnicode` w wyjściu), więc polskie znaki „ł ą ę ś ź ż ć ń" wychodzą
 * zepsute. pdfmake osadza Roboto z Latin-Ext i generuje mapę `ToUnicode`.
 */

import type { PdfMakeApi } from 'pdfmake/build/pdfmake';

export type ExportColumn = { header: string; key: string };
export type ExportRow = Record<string, string | number | null | undefined>;
export type FormatEksportu = 'csv' | 'txt' | 'xlsx' | 'pdf';

/**
 * GÓRNE LIMITY EKSPORTU — RÓŻNE PER FORMAT, bo ograniczenia są zupełnie różne.
 *
 * Do 13.08.2026 wszystkie trzy formaty dzieliły jedną liczbę 20 000 i to była zła liczba: raport
 * wydań za kwartał u większego klienta przekracza ją bez trudu, a CSV nie ma z tym najmniejszego
 * problemu — problem miał tylko nasz kod. Stąd rozdzielenie:
 *
 * - **CSV** — format nie ma żadnego limitu, jedyną granicą jest pamięć przeglądarki. 250 000 wierszy
 *   po ~15 kolumnach to rzędu 30–40 MB tekstu; `Blob` zszywa to sam, bez kopii w JS (patrz
 *   `buildCsvBlob`).
 * - **XLSX** — `exceljs` buduje w pamięci pełny model obiektowy KAŻDEJ komórki, a potem pakuje go
 *   zipem. 100 000 wierszy × 15 kolumn to już 1,5 mln obiektów; wyżej karta przeglądarki realnie
 *   umiera w połowie zapisu, czyli użytkownik traci plik po kilku minutach czekania.
 * - **PDF** — zostaje na 20 000, czyli tam gdzie było. Nie obniżamy limitu, który komuś dziś
 *   działa, choć 20 000 wierszy to około 500 stron i `PDF_TIMEOUT_MS` może się o to otrzeć.
 */
export const LIMIT_EKSPORTU: Record<FormatEksportu, number> = {
  csv: 250_000,
  /** ⚠️ Ten sam limit co CSV i z tego samego powodu: format nie ogranicza niczego poza pamięcią. */
  txt: 250_000,
  xlsx: 100_000,
  pdf: 20_000,
};

/**
 * Twardy limit ARKUSZA Excela — 2^20 wierszy, minus wiersz nagłówka.
 *
 * ⚠️ To granica formatu, nie naszej wygody: powyżej niej `exceljs` zapisze plik, który Excel
 * odrzuci jako uszkodzony. Sprawdzamy ją niezależnie od `LIMIT_EKSPORTU`, żeby podniesienie
 * tamtej liczby nie mogło wyprodukować pliku, którego nie da się otworzyć.
 */
export const EXCEL_MAX_WIERSZY = 1_048_576 - 1;

/** Ile wierszy bierzemy w jednym żądaniu przy eksporcie (`pageSize` list to maksymalnie tyle). */
export const STRONA_EKSPORTU = 5_000;

/**
 * Zbiera CAŁY przefiltrowany zbiór stronami — i ODMAWIA, ZANIM zacznie ściągać, gdy jest za duży.
 *
 * ⚠️ ODMOWA PRZED POBRANIEM, nie po. Pierwsza strona przynosi `total`, więc o „za dużo" wiadomo po
 * jednym żądaniu — dotąd `Raportowanie` ściągało 20 000 wierszy i dopiero wtedy rzucało błąd
 * (kilkadziesiąt zapytań do bazy po nic), a `Transakcje` ucinały zbiór po cichu i oddawały plik
 * wyglądający na kompletny, z samym dymkiem obok.
 *
 * ⚠️ Warunek wyjścia patrzy na PUSTĄ STRONĘ, a nie tylko na `total`. Wydania dochodzą w trakcie
 * eksportu, więc `total` może rosnąć między żądaniami — pętla oparta wyłącznie na nim nie miałaby
 * gwarancji zakończenia.
 */
export async function zbierzWszystkieStrony<T>(opts: {
  limit: number;
  pobierz: (page: number, pageSize: number) => Promise<{ rows: T[]; total: number }>;
  zaDuzo: (total: number, limit: number) => Error;
  postep?: (ile: number, zIlu: number) => void;
  stronaWierszy?: number;
}): Promise<T[]> {
  const rozmiar = opts.stronaWierszy ?? STRONA_EKSPORTU;
  const zebrane: T[] = [];

  for (let page = 1; ; page += 1) {
    const strona = await opts.pobierz(page, rozmiar);
    if (page === 1 && strona.total > opts.limit) throw opts.zaDuzo(strona.total, opts.limit);

    zebrane.push(...strona.rows);
    opts.postep?.(zebrane.length, strona.total);

    if (strona.rows.length < rozmiar || zebrane.length >= strona.total) break;
    /**
     * Zapadka na wypadek zbioru rosnącego szybciej, niż go czytamy: bez niej pętla mogłaby
     * chodzić dowolnie długo, a użytkownik widziałby tylko kręcące się kółko.
     */
    if (zebrane.length > opts.limit) throw opts.zaDuzo(zebrane.length, opts.limit);
  }
  return zebrane;
}

const asText = (v: ExportRow[string]): string => (v === null || v === undefined ? '' : String(v));

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---------- CSV ----------

export function buildCsvBlob(columns: ExportColumn[], rows: ExportRow[]): Blob {
  const escape = (v: string): string => (/[";\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  /**
   * ⚠️ CZĘŚCI ODDAJEMY DO `Blob` OSOBNO, bez `join`.
   *
   * `join('\r\n')` składał jeden napis obejmujący cały plik, a `'﻿' + …` robił z niego drugą kopię
   * — czyli przy dużym eksporcie trzykrotność danych w pamięci naraz (tablica + napis + kopia).
   * `Blob` przyjmuje tablicę fragmentów i zszywa je po swojej stronie, więc kopii w JS nie ma
   * ani jednej. Bez tego podniesienie limitu do 250 000 wierszy byłoby wymianą jednego błędu
   * na drugi: zamiast odmowy dostawalibyśmy zakładkę bez pamięci.
   */
  const czesci: string[] = ['﻿' + columns.map((c) => escape(c.header)).join(';')];
  for (const r of rows) {
    czesci.push('\r\n' + columns.map((c) => escape(asText(r[c.key]))).join(';'));
  }
  // separator ';' + BOM — Excel w polskim locale otwiera bez kreatora importu
  return new Blob(czesci, { type: 'text/csv;charset=utf-8' });
}

// ---------- TXT (tab delimited) ----------

/**
 * TAB DELIMITED (`.txt`) — plik dla PROGRAMU, nie dla człowieka.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️ POWSTAŁ NA ZGŁOSZENIE (31.08.2026): „MJM ma program, gdzie zaciągają txt tab delimitowany
 *  i nie potrzebują nagłówków". Adresatem pliku jest parser, i z tego wynika KAŻDA decyzja
 *  poniżej — inna niż w CSV, którego adresatem jest Excel i człowiek.
 *
 *  ⚠️⚠️ BEZ BOM, odwrotnie niż w CSV. BOM to trzy niewidzialne bajty w pierwszej wartości
 *  pierwszej kolumny: program porównujący nagłówek albo parsujący pierwsze pole dostaje śmieć
 *  i nie ma jak zgłosić, o co mu chodzi. W CSV BOM jest OBOWIĄZKOWY, bo bez niego Excel rozjeżdża
 *  polskie znaki — te dwa formaty mają sprzeczne wymagania i dlatego są dwiema funkcjami.
 *
 *  ⚠️⚠️ NIE CYTUJEMY, ZAMIENIAMY. Cytowanie w stylu CSV jest w tab delimited konwencją NIEZNANĄ
 *  większości parserów — cudzysłowy wjechałyby do danych. Tabulator i koniec wiersza w wartości
 *  MUSZĄ jednak zniknąć, bo rozjechałyby kolumny albo wiersze; zamieniamy je na spację.
 *
 *  ⚠️ REGUŁY IDENTYCZNE Z `api/src/reports/plik-raportu.ts` (`zbudujTxt`) i pilnowane testem.
 *  Ten sam raport pobrany z ekranu i przysłany mailem nie może różnić się ani separatorem, ani
 *  kodowaniem — inaczej jeden wjedzie do programu klienta, a drugi nie, i to bez błędu po drodze.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function komorkaTxt(v: string): string {
  return v.replace(/[\t\r\n]+/g, ' ');
}

export function buildTxtBlob(columns: ExportColumn[], rows: ExportRow[], naglowki: boolean): Blob {
  const czesci: string[] = [];
  if (naglowki) czesci.push(columns.map((c) => komorkaTxt(c.header)).join('\t'));
  for (const r of rows) {
    /**
     * ⚠️ SEPARATOR WIERSZA PRZED WARTOŚCIAMI, nie po. Plik bez nagłówków nie może zaczynać się
     * pustym wierszem — parser policzyłby go jako wiersz danych z pustymi polami.
     */
    const linia = columns.map((c) => komorkaTxt(asText(r[c.key]))).join('\t');
    czesci.push(czesci.length === 0 ? linia : '\r\n' + linia);
  }
  /** ⚠️ `text/plain`, nie `text/csv` — przeglądarka nie ma proponować otwarcia w arkuszu. */
  return new Blob(czesci, { type: 'text/plain;charset=utf-8' });
}

// ---------- Excel ----------

export async function buildXlsxBlob(
  columns: ExportColumn[],
  rows: ExportRow[],
  sheetName = 'Dane',
): Promise<Blob> {
  if (rows.length > EXCEL_MAX_WIERSZY) {
    throw new Error(
      `Arkusz Excela mieści ${EXCEL_MAX_WIERSZY.toLocaleString('pl-PL')} wierszy, a raport ma ` +
        `${rows.length.toLocaleString('pl-PL')}. Wybierz CSV — ten formatu nie ogranicza.`,
    );
  }
  const ExcelJS = await import('exceljs');
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(sheetName.slice(0, 31));

  ws.columns = columns.map((c) => ({
    header: c.header,
    key: c.key,
    width: Math.min(40, Math.max(12, c.header.length + 4)),
  }));
  ws.getRow(1).font = { bold: true };
  ws.views = [{ state: 'frozen', ySplit: 1 }];

  for (const r of rows) {
    ws.addRow(Object.fromEntries(columns.map((c) => [c.key, r[c.key] ?? ''])));
  }

  const buf = await wb.xlsx.writeBuffer();
  return new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

// ---------- PDF ----------

const PDF_TIMEOUT_MS = 30_000;

const ROBOTO = {
  normal: 'Roboto-Regular.ttf',
  bold: 'Roboto-Medium.ttf',
  italics: 'Roboto-Italic.ttf',
  bolditalics: 'Roboto-MediumItalic.ttf',
};

/**
 * SKŁADANIE PLIKU PDF Z GOTOWEGO DOKUMENTU pdfmake.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️ WYDZIELONE, BO PUŁAPKI SĄ TU, A NIE W UKŁADZIE: rejestracja czcionek różni się między
 *  pdfmake 0.2 i 0.3, a przy złej rejestracji `getBlob()` NIE ZGŁASZA BŁĘDU — po prostu nigdy
 *  nie woła callbacku. Druga mapa czy raport nie mogą tej wiedzy przepisywać od nowa; kopia
 *  znaczyłaby, że jeden eksport wisi w nieskończoność, a drugi nie.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export async function zlozPdf(doc: unknown): Promise<Blob> {
  const [pdfModule, vfsRaw] = await Promise.all([
    import('pdfmake/build/pdfmake'),
    import('pdfmake/build/vfs_fonts'),
  ]);

  // interop: bundler może oddać moduł pod `default` albo płasko
  const pdfMake: PdfMakeApi = pdfModule.default ?? (pdfModule as unknown as PdfMakeApi);

  const vfsModule = (vfsRaw as { default?: unknown }).default ?? vfsRaw;
  const vfs = ((vfsModule as { vfs?: Record<string, string> }).vfs ?? vfsModule) as Record<string, string>;

  if (!vfs[ROBOTO.normal]) {
    throw new Error(`Brak czcionki ${ROBOTO.normal} w pakiecie pdfmake — nie mogę złożyć PDF`);
  }

  /**
   * pdfmake **0.3** rejestruje czcionki funkcjami `addVirtualFileSystem`/`addFonts`.
   * W 0.2 były to właściwości `vfs`/`fonts`. Ustawienie właściwości na 0.3 jest po cichu
   * ignorowane → brak czcionki → `getBlob()` NIGDY nie woła callbacku (zawieszony eksport).
   * Dlatego preferujemy funkcje i mamy fallback na starsze API.
   */
  const isV3 = typeof pdfMake.addVirtualFileSystem === 'function' && typeof pdfMake.addFonts === 'function';

  if (isV3) {
    pdfMake.addVirtualFileSystem!(vfs);
    pdfMake.addFonts!({ Roboto: ROBOTO });
  } else {
    pdfMake.vfs = vfs;
    pdfMake.fonts = { Roboto: ROBOTO };
  }

  const created = pdfMake.createPdf(doc);

  /**
   * 0.3 ma `async getBlob()` (Promise), 0.2 — `getBlob(cb)`. Podanie callbacku wersji 0.3
   * kończy się CISZĄ: callback nigdy nie zostanie wywołany.
   */
  const generate = isV3
    ? (created.getBlob() as Promise<Blob>)
    : new Promise<Blob>((resolve) => {
        created.getBlob(resolve);
      });

  // Twardy timeout — pdfmake przy złej rejestracji czcionek nie zgłasza błędu, tylko milczy,
  // a bez tego przycisk eksportu zostawałby zablokowany na zawsze.
  const timeout = new Promise<never>((_, reject) =>
    setTimeout(
      () => reject(new Error('Generowanie PDF przekroczyło limit czasu (sprawdź rejestrację czcionek pdfmake)')),
      PDF_TIMEOUT_MS,
    ),
  );

  return Promise.race([generate, timeout]);
}

/** Tabela na A4 — zwykły eksport listy. Mapa planogramu składa własny dokument. */
export async function buildPdfBlob(columns: ExportColumn[], rows: ExportRow[], title: string): Promise<Blob> {
  return zlozPdf({
    pageSize: 'A4',
    pageOrientation: columns.length > 5 ? 'landscape' : 'portrait',
    pageMargins: [28, 32, 28, 36],
    defaultStyle: { font: 'Roboto', fontSize: 8 },
    content: [
      { text: title, fontSize: 13, bold: true, margin: [0, 0, 0, 10] },
      {
        table: {
          headerRows: 1,
          widths: columns.map(() => '*'),
          body: [
            columns.map((c) => ({ text: c.header, bold: true, fillColor: '#e8ecf5' })),
            ...rows.map((r) => columns.map((c) => asText(r[c.key]))),
          ],
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => '#c9cfdd',
          vLineColor: () => '#c9cfdd',
          fillColor: (rowIndex: number) => (rowIndex > 0 && rowIndex % 2 === 0 ? '#f6f7fa' : null),
        },
      },
    ],
  });
}

// ---------- fasada ----------

export function exportCsv(columns: ExportColumn[], rows: ExportRow[], filename: string): void {
  downloadBlob(buildCsvBlob(columns, rows), `${filename}.csv`);
}

export function exportTxt(columns: ExportColumn[], rows: ExportRow[], filename: string, naglowki: boolean): void {
  downloadBlob(buildTxtBlob(columns, rows, naglowki), `${filename}.txt`);
}

export async function exportXlsx(columns: ExportColumn[], rows: ExportRow[], filename: string): Promise<void> {
  downloadBlob(await buildXlsxBlob(columns, rows, filename), `${filename}.xlsx`);
}

export async function exportPdf(
  columns: ExportColumn[],
  rows: ExportRow[],
  filename: string,
  title: string,
): Promise<void> {
  downloadBlob(await buildPdfBlob(columns, rows, title), `${filename}.pdf`);
}
